// pages/PayrollPage.jsx
import { useState, useEffect } from 'react';
import payrollController from '../../controllers/payrollController';
import Payroll from '../../models/Payroll';
import { 
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, ChevronUp, ChevronDown,
  DollarSign, TrendingUp, Users, CheckCircle, Clock, FileText, Send, CreditCard
} from 'lucide-react';

const PayrollPage = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [filteredPayrolls, setFilteredPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterMonth, setFilterMonth] = useState('All');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [formData, setFormData] = useState({
    employeeId: '',
    month: '',
    year: new Date().getFullYear(),
    basicSalary: 0,
    allowances: 0,
    bonuses: 0,
    deductions: 0,
    tax: 0,
    workingDays: 26,
    presentDays: 26,
    leaveDays: 0,
    overtime: 0,
    status: 'Draft',
    paymentMethod: 'Bank Transfer',
    remarks: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    fetchPayrolls();
  }, []);

  useEffect(() => {
    let result = [...payrolls];
    
    if (searchQuery) {
      result = result.filter(p =>
        p.employeeId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.month?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.status?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (filterStatus !== 'All') {
      result = result.filter(p => p.status === filterStatus);
    }
    
    if (filterMonth !== 'All') {
      result = result.filter(p => p.month === filterMonth);
    }
    
    if (filterYear) {
      result = result.filter(p => p.year === parseInt(filterYear));
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredPayrolls(result);
    setCurrentPage(1);
  }, [payrolls, searchQuery, filterStatus, filterMonth, filterYear, sortField, sortDirection]);

  const fetchPayrolls = async () => {
    setLoading(true);
    const result = await payrollController.getAll({ page: 1, limit: 100 });
    if (result.success) {
      setPayrolls(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
    setFormErrors([]);
  };

  const calculateSalary = () => {
    return payrollController.calculateSalary(formData);
  };

  const handleAdd = () => {
    setFormData({
      employeeId: '',
      month: '',
      year: new Date().getFullYear(),
      basicSalary: 0,
      allowances: 0,
      bonuses: 0,
      deductions: 0,
      tax: 0,
      workingDays: 26,
      presentDays: 26,
      leaveDays: 0,
      overtime: 0,
      status: 'Draft',
      paymentMethod: 'Bank Transfer',
      remarks: ''
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);

    const result = await payrollController.create(formData);
    
    if (result.success) {
      setShowAddModal(false);
      fetchPayrolls();
      alert('✅ Payroll created successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    
    setFormSubmitting(false);
  };

  const handleEdit = (payroll) => {
    setSelectedPayroll(payroll);
    setFormData({
      employeeId: payroll.employeeId,
      month: payroll.month,
      year: payroll.year,
      basicSalary: payroll.basicSalary,
      allowances: payroll.allowances,
      bonuses: payroll.bonuses,
      deductions: payroll.deductions,
      tax: payroll.tax,
      workingDays: payroll.workingDays,
      presentDays: payroll.presentDays,
      leaveDays: payroll.leaveDays,
      overtime: payroll.overtime,
      status: payroll.status,
      paymentMethod: payroll.paymentMethod,
      remarks: payroll.remarks
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);

    const result = await payrollController.update(selectedPayroll.id, formData);
    
    if (result.success) {
      setShowEditModal(false);
      fetchPayrolls();
      alert('✅ Payroll updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    
    setFormSubmitting(false);
  };

  const handleView = (payroll) => {
    setSelectedPayroll(payroll);
    setShowViewModal(true);
  };

  const handleDelete = async (payroll) => {
    const isConfirmed = window.confirm(`Delete payroll for ${payroll.month} ${payroll.year}?`);
    if (!isConfirmed) return;

    const result = await payrollController.delete(payroll.id);
    
    if (result.success) {
      fetchPayrolls();
      alert('✅ Payroll deleted successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleProcessPayment = async (payroll) => {
    const isConfirmed = window.confirm(`Process payment for ${payroll.month} ${payroll.year}?`);
    if (!isConfirmed) return;

    const result = await payrollController.processPayment(payroll.id, {
      paymentMethod: 'Bank Transfer',
      remarks: 'Payment processed'
    });
    
    if (result.success) {
      fetchPayrolls();
      alert('✅ Payment processed successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleDownloadPayslip = async (payroll) => {
    const result = await payrollController.downloadPayslip(payroll.id);
    
    if (result.success) {
      alert('✅ Payslip downloaded!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const result = await payrollController.export({ status: filterStatus, month: filterMonth, year: filterYear }, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Payroll exported!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPayrolls = filteredPayrolls.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPayrolls.length / itemsPerPage);

  const stats = {
    total: payrolls.length,
    totalAmount: payrolls.reduce((sum, p) => sum + p.netSalary, 0),
    pending: payrolls.filter(p => p.status === 'Pending').length,
    paid: payrolls.filter(p => p.status === 'Paid').length,
    draft: payrolls.filter(p => p.status === 'Draft').length
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const styles = {
    container: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' },
    secondaryButton: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' },
    select: { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
    actionButton: { padding: '6px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', transition: 'background-color 0.2s' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' },
    textarea: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', resize: 'vertical', minHeight: '80px', fontFamily: 'inherit' },
    errorList: { backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
    errorItem: { color: '#dc2626', fontSize: '14px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
    modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
    salaryBreakdown: { background: '#f8fafc', padding: '16px', borderRadius: '8px', marginTop: '16px' }
  };

  const salary = calculateSalary();

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Payroll Management</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={handleExport}>
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Add Payroll
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Payrolls</div>
            <FileText size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Amount</div>
            <DollarSign size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981', fontSize: '22px' }}>
            {formatCurrency(stats.totalAmount)}
          </div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Pending</div>
            <Clock size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.pending}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Paid</div>
            <CheckCircle size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.paid}</div>
        </div>

        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Draft</div>
            <FileText size={24} color="#6b7280" />
          </div>
          <div style={{ ...styles.statValue, color: '#6b7280' }}>{stats.draft}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search payrolls..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Pending">Pending</option>
          <option value="Paid">Paid</option>
          <option value="Failed">Failed</option>
        </select>
        
        <select style={styles.select} value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
          <option value="All">All Months</option>
          {months.map(month => (
            <option key={month} value={month}>{month}</option>
          ))}
        </select>

        <select style={styles.select} value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
          <option value="">All Years</option>
          {[2024, 2023, 2022, 2021].map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* Payroll Table */}
      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            Loading payrolls...
          </div>
        ) : currentPayrolls.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            No payrolls found. Click "Add Payroll" to create one.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('month')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Period
                      {sortField === 'month' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Employee</th>
                  <th style={styles.th} onClick={() => handleSort('basicSalary')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Basic
                      {sortField === 'basicSalary' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Gross</th>
                  <th style={styles.th}>Deductions</th>
                  <th style={styles.th} onClick={() => handleSort('netSalary')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Net Salary
                      {sortField === 'netSalary' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentPayrolls.map((payroll) => {
                  const gross = payroll.calculateGrossSalary();
                  const totalDeductions = payroll.deductions + payroll.tax;
                  
                  return (
                    <tr key={payroll.id} style={{ cursor: 'pointer' }} onClick={() => handleView(payroll)}>
                      <td style={styles.td}>
                        <div style={{ fontWeight: '500' }}>{payroll.month} {payroll.year}</div>
                      </td>
                      <td style={styles.td}>{payroll.employeeId}</td>
                      <td style={styles.td}>{formatCurrency(payroll.basicSalary)}</td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: '500', color: '#10b981' }}>{formatCurrency(gross)}</div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ color: '#ef4444' }}>{formatCurrency(totalDeductions)}</div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: '600', fontSize: '15px', color: '#3b82f6' }}>
                          {formatCurrency(payroll.netSalary)}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: `${payroll.getStatusColor()}20`, color: payroll.getStatusColor() }}>
                          {payroll.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleView(payroll); }} title="View">
                            <Eye size={18} color="#64748b" />
                          </button>
                          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleEdit(payroll); }} title="Edit">
                            <Edit2 size={18} color="#3b82f6" />
                          </button>
                          {payroll.status === 'Pending' && (
                            <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleProcessPayment(payroll); }} title="Process Payment">
                              <CreditCard size={18} color="#10b981" />
                            </button>
                          )}
                          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleDelete(payroll); }} title="Delete">
                            <Trash2 size={18} color="#ef4444" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredPayrolls.length)} of {filteredPayrolls.length} payrolls
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Add New Payroll' : 'Edit Payroll'}</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Employee ID *</label>
                    <input type="text" name="employeeId" style={styles.input} value={formData.employeeId} onChange={handleInputChange} required />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Month *</label>
                    <select name="month" style={styles.input} value={formData.month} onChange={handleInputChange} required>
                      <option value="">Select Month</option>
                      {months.map(month => (
                        <option key={month} value={month}>{month}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Year *</label>
                    <input type="number" name="year" style={styles.input} value={formData.year} onChange={handleInputChange} required min="2020" max="2030" />
                  </div>
                </div>

                <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '20px', marginTop: '8px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>Salary Components</h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Basic Salary *</label>
                      <input type="number" name="basicSalary" style={styles.input} value={formData.basicSalary} onChange={handleInputChange} required min="0" />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Allowances</label>
                      <input type="number" name="allowances" style={styles.input} value={formData.allowances} onChange={handleInputChange} min="0" />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Bonuses</label>
                      <input type="number" name="bonuses" style={styles.input} value={formData.bonuses} onChange={handleInputChange} min="0" />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Deductions</label>
                      <input type="number" name="deductions" style={styles.input} value={formData.deductions} onChange={handleInputChange} min="0" />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Tax</label>
                      <input type="number" name="tax" style={styles.input} value={formData.tax} onChange={handleInputChange} min="0" />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Overtime Hours</label>
                      <input type="number" name="overtime" style={styles.input} value={formData.overtime} onChange={handleInputChange} min="0" />
                    </div>
                  </div>
                </div>

                <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '20px', marginTop: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>Attendance</h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Working Days</label>
                      <input type="number" name="workingDays" style={styles.input} value={formData.workingDays} onChange={handleInputChange} min="0" max="31" />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Present Days</label>
                      <input type="number" name="presentDays" style={styles.input} value={formData.presentDays} onChange={handleInputChange} min="0" max="31" />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Leave Days</label>
                      <input type="number" name="leaveDays" style={styles.input} value={formData.leaveDays} onChange={handleInputChange} min="0" max="31" />
                    </div>
                  </div>
                </div>

                <div style={styles.salaryBreakdown}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#475569' }}>Salary Calculation</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                    <div>Gross Salary:</div>
                    <div style={{ fontWeight: '600', color: '#10b981', textAlign: 'right' }}>{formatCurrency(salary.grossSalary)}</div>
                    <div>Total Deductions:</div>
                    <div style={{ fontWeight: '600', color: '#ef4444', textAlign: 'right' }}>{formatCurrency(formData.deductions + formData.tax)}</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', paddingTop: '8px', borderTop: '2px solid #cbd5e1' }}>Net Salary:</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#3b82f6', textAlign: 'right', paddingTop: '8px', borderTop: '2px solid #cbd5e1' }}>{formatCurrency(salary.netSalary)}</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}>
                      <option value="Draft">Draft</option>
                      <option value="Pending">Pending</option>
                      <option value="Paid">Paid</option>
                      <option value="Failed">Failed</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Payment Method</label>
                    <select name="paymentMethod" style={styles.input} value={formData.paymentMethod} onChange={handleInputChange}>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cash">Cash</option>
                      <option value="Cheque">Cheque</option>
                      <option value="UPI">UPI</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Remarks</label>
                  <textarea name="remarks" style={styles.textarea} value={formData.remarks} onChange={handleInputChange} placeholder="Add any remarks or notes..."></textarea>
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }} disabled={formSubmitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Create Payroll' : 'Update Payroll')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedPayroll && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Payroll Details - {selectedPayroll.month} {selectedPayroll.year}</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => setShowViewModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Employee ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedPayroll.employeeId}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Period</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedPayroll.month} {selectedPayroll.year}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ ...styles.badge, backgroundColor: `${selectedPayroll.getStatusColor()}20`, color: selectedPayroll.getStatusColor() }}>
                    {selectedPayroll.status}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Payment Method</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedPayroll.paymentMethod}</div>
                </div>
              </div>

              <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>Salary Breakdown</h3>
                <div style={{ display: 'grid', gap: '12px', fontSize: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0' }}>
                    <span>Basic Salary:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(selectedPayroll.basicSalary)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0' }}>
                    <span>Allowances:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(selectedPayroll.allowances)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0' }}>
                    <span>Bonuses:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(selectedPayroll.bonuses)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: '2px solid #cbd5e1' }}>
                    <span style={{ fontWeight: '600', color: '#10b981' }}>Gross Salary:</span>
                    <span style={{ fontWeight: '700', color: '#10b981' }}>{formatCurrency(selectedPayroll.calculateGrossSalary())}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ color: '#ef4444' }}>Deductions:</span>
                    <span style={{ fontWeight: '600', color: '#ef4444' }}>-{formatCurrency(selectedPayroll.deductions)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '12px', borderBottom: '2px solid #cbd5e1' }}>
                    <span style={{ color: '#ef4444' }}>Tax:</span>
                    <span style={{ fontWeight: '600', color: '#ef4444' }}>-{formatCurrency(selectedPayroll.tax)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px' }}>
                    <span style={{ fontSize: '16px', fontWeight: '700', color: '#3b82f6' }}>Net Salary:</span>
                    <span style={{ fontSize: '18px', fontWeight: '700', color: '#3b82f6' }}>{formatCurrency(selectedPayroll.netSalary)}</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Working Days</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedPayroll.workingDays}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Present Days</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#10b981' }}>{selectedPayroll.presentDays}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Leave Days</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#f59e0b' }}>{selectedPayroll.leaveDays}</div>
                </div>
              </div>

              {selectedPayroll.remarks && (
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Remarks</div>
                  <div style={{ fontSize: '14px', color: '#1e293b', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', lineHeight: '1.6' }}>
                    {selectedPayroll.remarks}
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => handleDownloadPayslip(selectedPayroll)}>
                <Download size={16} /> Download Payslip
              </button>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowViewModal(false); handleEdit(selectedPayroll); }}>
                <Edit2 size={16} /> Edit Payroll
              </button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollPage;